import FileService from "#src/components/Services/FileService";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import {
  fetchFileDownloadMetaWithScanPolling,
  isFileScanTerminal,
} from "#src/utils/fileDownload";
import { type FileUploadInputProps } from "@validereinc/common-components";
import { FileUploadedSchema } from "@validereinc/domain";
import {
  getMimeTypeFromFileName,
  invariant,
  SchemaValidationError,
} from "@validereinc/utilities";

export const useFile = (params?: {
  downloadConfig?: { maxPolls?: number; pollSleepTimeMs?: number };
  uploadConfig?: { maxPolls?: number; pollSleepTime?: number };
}) => {
  const { claims } = useAuthenticatedContext();

  const handleFileDownload: FileUploadInputProps["downloadFile"] = async (
    value: unknown | undefined,
    { setStatus, signal }
  ) => {
    if (!value) {
      return;
    }

    const valueVerified = FileUploadedSchema.safeParse(value);

    if (!valueVerified.success) {
      throw new SchemaValidationError("Value is invalid file uploaded shape", {
        props: {
          parseError: valueVerified.error,
        },
      });
    }

    const mimeType = getMimeTypeFromFileName(valueVerified.data.name);

    invariant(mimeType, "File name is invalid");

    setStatus({ percentComplete: 1, label: "Verified..." });

    setStatus({ percentComplete: 25, label: "Downloading..." });

    // poll for the file's scan status, since it may still be pending shortly
    // after upload. Poll indefinitely by default so the preview keeps waiting
    // for a slow scan; the signal aborts polling when the preview unmounts.
    const fileData = await fetchFileDownloadMetaWithScanPolling(
      valueVerified.data.ref,
      {
        maxPolls: params?.downloadConfig?.maxPolls ?? Infinity,
        pollSleepTimeMs: params?.downloadConfig?.pollSleepTimeMs,
        signal,
      }
    );

    setStatus({ percentComplete: 55, label: "Verifying integrity..." });

    invariant(
      fileData.file_scan_status === "safe",
      "The file download link is not available."
    );

    const { uri: downloadUrl } = fileData;

    invariant(downloadUrl, "The file download URL does not exist.");

    setStatus({ percentComplete: 60, label: "Fetching file..." });

    const data = await fetch(downloadUrl, {
      signal: signal ?? undefined,
    }).then((r) => r.blob());

    setStatus({ percentComplete: 100, label: "Downloaded." });

    return new File([data], valueVerified.data.name, {
      type: mimeType,
    });
  };

  const handleFileUpload: FileUploadInputProps["uploadFile"] = async (
    file,
    { setStatus, signal }
  ): Promise<{ ref: string; name: string } | undefined> => {
    if (!file) return undefined;

    const { name, type: contentType, lastModified } = file;

    setStatus({ percentComplete: 0, label: "Uploading..." });

    const createdFile = await FileService.createFile({
      name,
      contentType,
      description:
        "A user-uploaded file through a file input component on the web",
      tags: {
        lastModified: new Date(lastModified).toISOString(),
      },
      companyId: claims.company.id,
      signal,
    });

    invariant(
      createdFile,
      "Could not create an entry in the file upload service"
    );

    const {
      status: createFileStatus,
      data: {
        data: [uploadedFileData],
      },
    } = createdFile;

    invariant(
      createFileStatus === 200 || createFileStatus === 201,
      "Could not create an entry in the file upload service"
    );

    // extract the ID and upload URL of the file entry in the file upload service
    const { file_id: fileId, uri: uploadUrl } = uploadedFileData;

    // upload the file to the pre-signed upload URL
    const uploadedFile = await FileService.uploadFile({
      url: uploadUrl,
      fileBlob: file,
      signal,
    });

    invariant(
      uploadedFile,
      "Could not upload file to the created entry in file upload service"
    );

    const { status: uploadFileStatus } = uploadedFile;

    invariant(
      uploadFileStatus === 200 || uploadFileStatus === 201,
      "Could not upload file to the created entry in file upload service"
    );

    // poll and check to see if the file scanning in the file upload service has completed
    setStatus({ percentComplete: 33, label: "Scanning..." });

    // poll until the scan verdict (safe/unsafe) is in. Poll indefinitely by
    // default so a slow scan keeps the upload waiting; the signal aborts polling
    // when the input unmounts or the upload is cancelled.
    const scannedFileData = await fetchFileDownloadMetaWithScanPolling(
      fileId as string,
      {
        isComplete: isFileScanTerminal,
        maxPolls: params?.uploadConfig?.maxPolls ?? Infinity,
        pollSleepTimeMs: params?.uploadConfig?.pollSleepTime,
        signal,
      }
    );

    // verify based on the scan result
    setStatus({ percentComplete: 66, label: "Verifying..." });

    invariant(
      scannedFileData.file_scan_status === "safe",
      "The uploaded file has been deemed as unsafe or failed to complete verification"
    );

    setStatus({ percentComplete: 100, label: "Complete" });

    return {
      ref: fileId as string,
      name,
    };
  };

  return { handleFileUpload, handleFileDownload };
};
